import { AnimatePresence, motion } from 'framer-motion';
import {
  ChevronDown,
  ChevronRight,
  CornerDownLeft,
  Crown,
  Folder,
  FolderOpen,
  Home,
  Layers3,
  LoaderCircle,
  Minus,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  User,
  UserCheck,
  UserMinus,
  Users,
} from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { formatDate, formatToken } from '../lib/format';
import { atomicToDecimal } from '../state/AppDataContext';
import type { MemberStatus, SlotType } from '../lib/lookup-types';
import { slotLabel } from '../lib/slots';

/** Mirrors `GenerationNodeView` in packages/shared/src/types.ts. */
export interface GenerationNode {
  bdId: string;
  sponsorBdId: string | null;
  birthEra: SlotType;
  status: MemberStatus;
  joinedAt: string;
  /** Highest real slot the contract activated (25 = ¼, 50 = ½, 1 .. 16); 0 = none. Display with slotLabel. */
  activeSlot: number;
  confirmedVolumeAtomic: string;
  /** Placed in the matrix on-chain (absent from older APIs). */
  placed?: boolean;
  directCount: number;
  teamCount: number;
}

/** Mirrors `GenerationChildrenView` in packages/shared/src/types.ts. */
interface GenerationChildrenPage {
  parent: GenerationNode;
  path: GenerationNode[];
  children: GenerationNode[];
  totalCount: number;
  nextOffset: number | null;
}

/** Cached, incrementally-grown children list for one node. */
interface Branch {
  nodes: GenerationNode[];
  totalCount: number;
  nextOffset: number | null;
  loading: boolean;
  error: string | null;
}

const PAGE_SIZE = 25;

/** How many generations may be opened below the focused root before drill-in is required. */
export const MAX_VISIBLE_DEPTH = 6;

function statusIcon(status: MemberStatus) {
  if (status === 'active') return UserCheck;
  if (status === 'suspended') return UserMinus;
  return User;
}

function statusTone(status: MemberStatus): string {
  if (status === 'active') return 'green';
  if (status === 'suspended') return 'red';
  return 'amber';
}

function eraLabel(era: SlotType): string {
  if (era === 'half') return 'Half';
  if (era === 'quarter') return 'Quarter';
  return 'Standard';
}

/** Short face for the node avatar: the numeric part of BD-42 is more scannable than the prefix. */
function nodeFace(bdId: string): string {
  return bdId.replace(/^BD-/i, '');
}

async function fetchChildren(bdId: string, offset: number): Promise<GenerationChildrenPage> {
  return api<GenerationChildrenPage>(
    `/api/public/network/${encodeURIComponent(bdId)}/children?limit=${PAGE_SIZE}&offset=${offset}`,
  );
}

/**
 * Lazily-loaded, drillable view of the sponsor graph.
 *
 * The previous tree rendered two hard-coded generations of a pre-fetched subtree, which made a
 * six-level network look two levels deep. This loads exactly one generation per request, keeps
 * every opened branch cached, and lets any downline ID become the focused root - with a
 * breadcrumb trail and a back control to walk the drill-ins back out, like a directory listing.
 */
export function GenerationExplorer({
  rootBdId,
  title = 'Generation explorer',
  eyebrow = 'SPONSOR GRAPH',
}: {
  rootBdId: string;
  title?: string;
  eyebrow?: string;
}) {
  // The drill-in stack. The last entry is the focused root; earlier entries are where we came from.
  const [stack, setStack] = useState<string[]>([rootBdId]);
  const [branches, setBranches] = useState<Record<string, Branch>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [focusNode, setFocusNode] = useState<GenerationNode | null>(null);
  const [ancestry, setAncestry] = useState<GenerationNode[]>([]);
  const [filter, setFilter] = useState('');
  const requestSeq = useRef(0);

  const focusedId = stack[stack.length - 1] ?? rootBdId;

  // A new lookup replaces the whole explorer: reset the stack and every cached branch.
  useEffect(() => {
    setStack([rootBdId]);
    setBranches({});
    setExpanded(new Set());
    setFocusNode(null);
    setAncestry([]);
    setFilter('');
  }, [rootBdId]);

  const loadBranch = useCallback(async (bdId: string, offset: number) => {
    setBranches((previous) => ({
      ...previous,
      [bdId]: {
        nodes: previous[bdId]?.nodes ?? [],
        totalCount: previous[bdId]?.totalCount ?? 0,
        nextOffset: previous[bdId]?.nextOffset ?? null,
        loading: true,
        error: null,
      },
    }));
    const seq = ++requestSeq.current;
    try {
      const page = await fetchChildren(bdId, offset);
      if (seq !== requestSeq.current && offset === 0) return page;
      setBranches((previous) => {
        const existing = offset === 0 ? [] : (previous[bdId]?.nodes ?? []);
        const seen = new Set(existing.map((node) => node.bdId));
        return {
          ...previous,
          [bdId]: {
            nodes: [...existing, ...page.children.filter((node) => !seen.has(node.bdId))],
            totalCount: page.totalCount,
            nextOffset: page.nextOffset,
            loading: false,
            error: null,
          },
        };
      });
      return page;
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'This generation could not be loaded.';
      setBranches((previous) => ({
        ...previous,
        [bdId]: {
          nodes: previous[bdId]?.nodes ?? [],
          totalCount: previous[bdId]?.totalCount ?? 0,
          nextOffset: previous[bdId]?.nextOffset ?? null,
          loading: false,
          error: message,
        },
      }));
      return null;
    }
  }, []);

  // Load the focused root's first generation (and its breadcrumb ancestry) whenever focus moves.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const cached = branches[focusedId];
      if (cached && !cached.error && cached.nodes.length > 0) {
        // Branch already cached; still need parent/ancestry if this is a fresh focus.
        if (focusNode?.bdId === focusedId) return;
      }
      const page = await loadBranch(focusedId, 0);
      if (!page || cancelled) return;
      setFocusNode(page.parent);
      setAncestry(page.path);
    })();
    return () => {
      cancelled = true;
    };
    // `branches` is intentionally excluded: it changes on every page append and would re-trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedId, loadBranch]);

  const toggle = useCallback(
    (node: GenerationNode) => {
      setExpanded((previous) => {
        const next = new Set(previous);
        if (next.has(node.bdId)) {
          next.delete(node.bdId);
          return next;
        }
        next.add(node.bdId);
        return next;
      });
      if (!branches[node.bdId] && node.directCount > 0) void loadBranch(node.bdId, 0);
    },
    [branches, loadBranch],
  );

  const drillInto = useCallback((bdId: string) => {
    setStack((previous) => (previous[previous.length - 1] === bdId ? previous : [...previous, bdId]));
    setExpanded(new Set());
    setFilter('');
  }, []);

  const goBack = useCallback(() => {
    setStack((previous) => (previous.length > 1 ? previous.slice(0, -1) : previous));
    setExpanded(new Set());
    setFilter('');
  }, []);

  const jumpTo = useCallback((index: number) => {
    setStack((previous) => previous.slice(0, index + 1));
    setExpanded(new Set());
    setFilter('');
  }, []);

  const expandAllVisible = useCallback(() => {
    const branch = branches[focusedId];
    if (!branch) return;
    setExpanded((previous) => {
      const next = new Set(previous);
      for (const node of branch.nodes) {
        if (node.directCount > 0) {
          next.add(node.bdId);
          if (!branches[node.bdId]) void loadBranch(node.bdId, 0);
        }
      }
      return next;
    });
  }, [branches, focusedId, loadBranch]);

  const rootBranch = branches[focusedId];
  const trail = useMemo(() => {
    // The stack is where the viewer has clicked; ancestry fills in the part above the entry point.
    const entryAncestors = ancestry.filter((node) => !stack.includes(node.bdId));
    return [...entryAncestors.map((node) => node.bdId), ...stack];
  }, [ancestry, stack]);

  const normalizedFilter = filter.trim().toLowerCase();
  const visibleNodes = useMemo(() => {
    const nodes = rootBranch?.nodes ?? [];
    if (!normalizedFilter) return nodes;
    return nodes.filter((node) => node.bdId.toLowerCase().includes(normalizedFilter));
  }, [rootBranch, normalizedFilter]);

  const generationCounts = useMemo(() => {
    // Level 1 is always known from the focused node; deeper levels reflect what has been opened.
    const counts: number[] = [];
    let frontier = rootBranch?.nodes ?? [];
    let level = 0;
    while (frontier.length > 0 && level < MAX_VISIBLE_DEPTH) {
      counts.push(frontier.length);
      const next: GenerationNode[] = [];
      for (const node of frontier) {
        if (expanded.has(node.bdId)) next.push(...(branches[node.bdId]?.nodes ?? []));
      }
      frontier = next;
      level += 1;
    }
    return counts;
  }, [rootBranch, expanded, branches]);

  return (
    <section className="panel generation-explorer">
      <div className="panel-heading gx-heading">
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h2>{title}</h2>
        </div>
        <div className="gx-heading-tools">
          <label className="gx-filter">
            <Search size={14} />
            <input
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Filter this generation"
              aria-label="Filter the current generation by ID"
            />
          </label>
          <button type="button" className="gx-tool" onClick={expandAllVisible} title="Expand every branch on this level">
            <Plus size={14} /> Expand
          </button>
          <button
            type="button"
            className="gx-tool"
            onClick={() => setExpanded(new Set())}
            disabled={expanded.size === 0}
            title="Collapse every open branch"
          >
            <Minus size={14} /> Collapse
          </button>
          <button
            type="button"
            className="gx-tool"
            onClick={() => void loadBranch(focusedId, 0)}
            title="Reload this generation"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      <nav className="gx-breadcrumbs" aria-label="Generation path">
        <button
          type="button"
          className="gx-crumb-back"
          onClick={goBack}
          disabled={stack.length <= 1}
          title="Back to the previous generation"
        >
          <CornerDownLeft size={14} /> Back
        </button>
        <div className="gx-crumb-trail">
          {trail.map((bdId, index) => {
            const stackIndex = stack.indexOf(bdId);
            const isCurrent = bdId === focusedId;
            const isAbove = stackIndex === -1;
            return (
              <Fragment key={`${bdId}-${index}`}>
                {index > 0 ? <ChevronRight size={12} className="gx-crumb-sep" aria-hidden /> : null}
                {isAbove ? (
                  <Link className="gx-crumb above" to={`/lookup?q=${bdId}`} title={`Open ${bdId} in lookup`}>
                    {index === 0 ? <Home size={12} /> : null}
                    {bdId}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className={`gx-crumb${isCurrent ? ' current' : ''}`}
                    onClick={() => jumpTo(stackIndex)}
                    aria-current={isCurrent ? 'page' : undefined}
                    title={isCurrent ? `${bdId} (current)` : `Return to ${bdId}`}
                  >
                    {index === 0 ? <Home size={12} /> : null}
                    {bdId}
                  </button>
                )}
              </Fragment>
            );
          })}
        </div>
      </nav>

      {focusNode ? (
        <div className="gx-focus">
          <div className="gx-focus-avatar" aria-hidden>
            {stack.length === 1 ? <Crown size={16} /> : <FolderOpen size={16} />}
          </div>
          <div className="gx-focus-body">
            <strong>{focusNode.bdId}</strong>
            <span>
              {eraLabel(focusNode.birthEra)} · joined {formatDate(focusNode.joinedAt)} · slot{' '}
              {focusNode.activeSlot !== 0 ? slotLabel(focusNode.activeSlot) : focusNode.placed === false ? 'not placed on-chain' : '—'}
            </span>
          </div>
          <div className="gx-focus-metrics">
            <span className="gx-metric">
              <Users size={13} /> {focusNode.directCount.toLocaleString()} direct
            </span>
            <span className="gx-metric">
              <Layers3 size={13} /> {focusNode.teamCount.toLocaleString()} team
            </span>
            <span className={`gx-metric tone-${statusTone(focusNode.status)}`}>
              <ShieldCheck size={13} /> {focusNode.status}
            </span>
          </div>
        </div>
      ) : null}

      {generationCounts.length > 0 ? (
        <div className="gx-levels" aria-label="Members per open generation">
          {generationCounts.map((count, index) => (
            <span key={index} className="gx-level-chip">
              <i>L{index + 1}</i>
              {count.toLocaleString()}
            </span>
          ))}
        </div>
      ) : null}

      <div className="gx-tree" role="tree" aria-label={`Downline of ${focusedId}`}>
        {rootBranch?.loading && (rootBranch.nodes.length === 0) ? (
          <p className="gx-status">
            <LoaderCircle className="spin" size={15} /> Loading generation…
          </p>
        ) : null}
        {rootBranch?.error ? (
          <p className="gx-status error">
            {rootBranch.error}{' '}
            <button type="button" className="gx-inline-button" onClick={() => void loadBranch(focusedId, 0)}>
              Retry
            </button>
          </p>
        ) : null}
        {!rootBranch?.loading && !rootBranch?.error && visibleNodes.length === 0 ? (
          <p className="gx-status">
            {normalizedFilter
              ? `No ID on this generation matches “${filter.trim()}”.`
              : `${focusedId} has no sponsored members yet — this branch ends here.`}
          </p>
        ) : null}

        {visibleNodes.map((node, index) => (
          <GenerationRow
            key={node.bdId}
            node={node}
            depth={0}
            index={index}
            expanded={expanded}
            branches={branches}
            onToggle={toggle}
            onDrill={drillInto}
            onLoadMore={(bdId, offset) => void loadBranch(bdId, offset)}
          />
        ))}

        {rootBranch && rootBranch.nextOffset !== null && !normalizedFilter ? (
          <button
            type="button"
            className="gx-load-more"
            onClick={() => void loadBranch(focusedId, rootBranch.nextOffset ?? 0)}
            disabled={rootBranch.loading}
          >
            {rootBranch.loading ? <LoaderCircle className="spin" size={14} /> : <ChevronDown size={14} />}
            Load {Math.min(PAGE_SIZE, rootBranch.totalCount - rootBranch.nodes.length)} more of{' '}
            {rootBranch.totalCount.toLocaleString()}
          </button>
        ) : null}
      </div>
    </section>
  );
}

function GenerationRow({
  node,
  depth,
  index,
  expanded,
  branches,
  onToggle,
  onDrill,
  onLoadMore,
}: {
  node: GenerationNode;
  depth: number;
  index: number;
  expanded: Set<string>;
  branches: Record<string, Branch>;
  onToggle(node: GenerationNode): void;
  onDrill(bdId: string): void;
  onLoadMore(bdId: string, offset: number): void;
}) {
  const isOpen = expanded.has(node.bdId);
  const branch = branches[node.bdId];
  const StatusIcon = statusIcon(node.status);
  const canExpand = node.directCount > 0;
  // Past the visible-depth budget the row stops nesting and offers a drill-in instead, so the
  // indentation can never run off the right edge of a phone.
  const atDepthLimit = depth + 1 >= MAX_VISIBLE_DEPTH;

  return (
    <div className="gx-branch" style={{ '--gx-depth': depth } as React.CSSProperties}>
      <motion.div
        className={`gx-row depth-${depth}${isOpen ? ' open' : ''}`}
        role="treeitem"
        aria-expanded={canExpand ? isOpen : undefined}
        initial={{ opacity: 0, x: -6 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.22, delay: Math.min(index * 0.02, 0.2) }}
      >
        <span className="gx-rail" aria-hidden />
        <button
          type="button"
          className={`gx-twisty${canExpand ? '' : ' leaf'}`}
          onClick={() => canExpand && !atDepthLimit && onToggle(node)}
          disabled={!canExpand || atDepthLimit}
          aria-label={canExpand ? `${isOpen ? 'Collapse' : 'Expand'} ${node.bdId}` : `${node.bdId} has no downline`}
          title={
            !canExpand
              ? 'No sponsored members'
              : atDepthLimit
                ? `Open ${node.bdId} to go deeper`
                : isOpen
                  ? 'Collapse'
                  : 'Expand'
          }
        >
          {canExpand ? isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} /> : <span className="gx-dot" />}
        </button>

        <span className={`gx-icon tone-${statusTone(node.status)}`} aria-hidden>
          {canExpand ? isOpen ? <FolderOpen size={15} /> : <Folder size={15} /> : <StatusIcon size={15} />}
        </span>

        <button type="button" className="gx-id" onClick={() => onDrill(node.bdId)} title={`Open ${node.bdId}'s generation`}>
          <i>{nodeFace(node.bdId)}</i>
          <strong>{node.bdId}</strong>
        </button>

        <span className="gx-tags">
          <span className="gx-tag">{eraLabel(node.birthEra)}</span>
          <span className={`gx-tag tone-${statusTone(node.status)}`}>{node.status}</span>
          {node.activeSlot !== 0 ? <span className="gx-tag slot" title="Highest slot activated on-chain">Slot {slotLabel(node.activeSlot)}</span> : null}
          {node.placed === false ? <span className="gx-tag" title="Created on-chain but not placed in the matrix">not placed</span> : null}
        </span>

        <span className="gx-counts">
          <span title="Direct sponsored members">
            <Users size={12} /> {node.directCount.toLocaleString()}
          </span>
          <span title="Total downline">
            <Layers3 size={12} /> {node.teamCount.toLocaleString()}
          </span>
          <span title="Confirmed volume">{formatToken(atomicToDecimal(node.confirmedVolumeAtomic))}</span>
        </span>

        <span className="gx-actions">
          <Link className="gx-action" to={`/lookup?q=${node.bdId}`} title={`Full record for ${node.bdId}`}>
            <Search size={13} />
          </Link>
          <button type="button" className="gx-action" onClick={() => onDrill(node.bdId)} title={`Drill into ${node.bdId}`}>
            <ChevronRight size={13} />
          </button>
        </span>
      </motion.div>

      <AnimatePresence initial={false}>
        {isOpen ? (
          <motion.div
            className="gx-children"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
          >
            {branch?.loading && branch.nodes.length === 0 ? (
              <p className="gx-status nested">
                <LoaderCircle className="spin" size={14} /> Loading {node.directCount} member
                {node.directCount === 1 ? '' : 's'}…
              </p>
            ) : null}
            {branch?.error ? (
              <p className="gx-status nested error">
                {branch.error}{' '}
                <button type="button" className="gx-inline-button" onClick={() => onLoadMore(node.bdId, 0)}>
                  Retry
                </button>
              </p>
            ) : null}
            {(branch?.nodes ?? []).map((child, childIndex) => (
              <GenerationRow
                key={child.bdId}
                node={child}
                depth={depth + 1}
                index={childIndex}
                expanded={expanded}
                branches={branches}
                onToggle={onToggle}
                onDrill={onDrill}
                onLoadMore={onLoadMore}
              />
            ))}
            {branch && branch.nextOffset !== null ? (
              <button
                type="button"
                className="gx-load-more nested"
                onClick={() => onLoadMore(node.bdId, branch.nextOffset ?? 0)}
                disabled={branch.loading}
              >
                {branch.loading ? <LoaderCircle className="spin" size={13} /> : <ChevronDown size={13} />}
                Load more ({branch.nodes.length}/{branch.totalCount})
              </button>
            ) : null}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
